import React from 'react';
import {
  getSystem,
  Plugins,
  Component,
  Expressions,
  matchExpression,
  TypeImplementation,
  Type,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  ELiteral,
  EElement,
  ERecord,
  EText,
  EList,
  TList,
  TElement,
} from '@packages/systems/wfdl-ast';
import {AddIcon, Tooltip} from '@packages/systems/spring';
import {useTheme} from '@packages/utilities/styled';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {BaseButton as Button} from '@packages/systems/design-system/Button';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {generateGuid} from '@packages/utilities/guid';
import * as StringUtils from '@packages/utilities/string-utils';
import {concat, constant} from '@packages/utilities/fp/utils';
import {getChildren} from '@packages/utilities/platform';
import {getNodeClickedActionCreatorFromState} from '@packages/systems/designer/canvas';
import {selectIsOnSecondaryLocale} from '@packages/systems/localization';
import {useTranslation} from '@packages/systems/designer-translations';
import {useDispatch, useFluxContext} from '@packages/systems/designer/flux';

type System = {
  label: string;
  labelVariant: 'hidden';
};
const dropdownLinkType = qualify(
  Plugins.name('Dropdown'),
  Component.name('DropdownLink')
);
const stringType = qualify(Plugins.name('Basic'), Component.name('String'));

const getLinkTexts = matchExpression({
  Element: (val) => {
    if (namesEqual(val.type, dropdownLinkType)) {
      const children = getChildren(val);
      if (children && children[0] && namesEqual(children[0].type, stringType))
        return Expressions.stop([Expressions.getText(children[0].data) || '']);
    }
    return Expressions.stop([]);
  },
  _: constant(Expressions.resume([])),
});
const validateTypeInDevelopment = (
  expr: ContextualExpression,
  type: null | Type
): System => {
  if (type === null) {
    throw Error('Expected type from AnnotatedExpression to be defined');
  }
  const system = getSystem(type);

  if (process.env.NODE_ENV === 'development') {
    if (system === null) {
      throw Error('Expected system from AnnotatedExpression to be defined');
    }

    if (!system.hasOwnProperty('label')) {
      throw Error('label is required');
    }

    if (
      !system.hasOwnProperty('labelVariant') ||
      system.labelVariant !== 'hidden'
    ) {
      throw Error('labelVariant is required to be `hidden`');
    }
  }
  return {label: (system && system.label) || '', labelVariant: 'hidden'};
};

const AddDropdownLink = TypeImplementation.Alias({
  type: TList(TElement),
  render: function AddDropdownLink({expr, dispatch}) {
    const flux = useFluxContext();
    const wfDispatch = useDispatch();
    const nodeClicked = getNodeClickedActionCreatorFromState(flux.state);
    const t = useTranslation();
    // Tree structure changes are not allowed in secondary locales
    const isOnSecondaryLocale = selectIsOnSecondaryLocale(
      flux.state.LocalizationStore
    );

    const {type} = getAnn(expr);
    const system = validateTypeInDevelopment(expr, type);

    const addLink = () => {
      const DropdownLinkExpr = (label: string, id: string) =>
        getSecond(
          instantiateExpressionWithUniqueIds(
            EElement({
              id: 'Dropdown Link',
              type: qualify(
                Plugins.name('Dropdown'),
                Component.name('DropdownLink')
              ),
              data: ERecord({
                dropdown: ERecord({
                  type: EText('link'),
                }),
                link: ELiteral({
                  name: qualify(Plugins.name('Basic'), typeName('Link')),
                  value: {mode: 'external', url: '#'},
                }),
                children: EList([
                  EElement({
                    id: 'String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText(label),
                  }),
                ]),
              }),
            })
          )(id)
        );
      // `stripAll` is super expensive here, tracking for alternate solutions:
      // https://webflow.atlassian.net/browse/LOGIC-3738
      const [DropdownToggle, DropdownList] = Expressions.getList(
        stripAll(expr)
      ) || [null, null];
      if (!DropdownList || !DropdownToggle) {
        throw Error('Unable to get list from Dropdown Expression');
      }

      const DListElement = Expressions.getElement(DropdownList);
      if (!DListElement) {
        throw Error('Unable to getElement from DropdownList Expression');
      }

      const DropdownListChildren = Expressions.getIn(
        Expressions.getElementData(DListElement),
        [{in: 'Record', at: 'children'}]
      );
      if (!DropdownListChildren) {
        throw Error(
          'Unable to getChildren from DropdownList Expression Element'
        );
      }

      const DLChildrenList = Expressions.getList(DropdownListChildren);
      if (!DLChildrenList) {
        throw Error('DropdownListChildren is not a list');
      }

      const otherLabels =
        Expressions.everything(concat)(getLinkTexts)(DropdownListChildren);

      const id = generateGuid();

      const newDropdownLink = DropdownLinkExpr(
        StringUtils.increment(otherLabels, 'Link'),
        id
      );

      const newDropdownListChildren = EList([
        ...DLChildrenList,
        newDropdownLink,
      ]);

      const newDropdownList = Expressions.setIn(
        DropdownList,
        [{in: 'Element'}, {in: 'Record', at: 'children'}],
        newDropdownListChildren
      );

      dispatch(
        updateExpressionAction(EList([DropdownToggle, newDropdownList]))
      );
      wfDispatch(
        nodeClicked({
          nativeIdInCurrentComponent: id,
        })
      );
    };

    const {gutters, control} = useTheme();

    return (
      <Hitarea data-automation-id="ToggleButtonAlias">
        <Tooltip
          label={t('designer.localization.disabled.tooltip')}
          isDisabled={!isOnSecondaryLocale}
        >
          <Button
            disabled={isOnSecondaryLocale}
            data-automation-id="ToggleButton"
            onClick={addLink}
            style={{
              gap: gutters.base,
              height: control.height,
            }}
          >
            <AddIcon />
            <span>{system.label}</span>
          </Button>
        </Tooltip>
      </Hitarea>
    );
  },
});

export default AddDropdownLink;
